import type { SagaIterator } from "redux-saga";
import { call, debounce, fork, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

import * as mainSearchApi from "@/shop/api/mainSearch";
import { MainSearchSource, MainSearchSuggestionsScope } from "@/shop/api/mainSearch";
import { removeDots } from "@/shop/components/MainSearch/helpers/removeDots";
import * as TrackerActions from "@/shop/tracker/actions";
import type { IPagingQuery } from "@/shop/types/searchTable";

import type { IMainSearchFilterDefinition, IMainSearchFilterSetting, IMainSearchResult } from "../../types/mainSearch";
import { getSelectedLanguage } from "../session";
import * as MainSearchActions from "./actions";
import { DEFAULT_PAGE_SIZE_MAIN_SEARCH_RESULTS } from "./constants";
import {
  getMainSearchResultsScopes,
  getMainSearchSuggestionResultsCount,
  getMainSearchSuggestionsScopes,
  getMainSearchSuggestionText,
  getMainSearchText,
  getPagingQuery,
  getSearchFiltersSetting,
  getSearchResultCount
} from "./selectors";

function normalizeMainSearchTerm(term: string, collection: MainSearchSource): string {
  return collection === MainSearchSource.OemSpareParts ? term : removeDots(term);
}

function* fetchSearchQuery(_: ActionType<typeof MainSearchActions.setSearchText>): SagaIterator {
  const scopes = (yield select(getMainSearchResultsScopes)) as MainSearchSource[];

  // clear results and filters
  for (const scope of scopes) {
    yield put(MainSearchActions.setFilterSetting({ payload: [], meta: { collection: scope } }));
    yield put(MainSearchActions.clearResults(null, { collection: scope }));
  }

  // fetch new results
  for (const scope of scopes) {
    yield put(MainSearchActions.fetchResults(null, { collection: scope, query: { limit: DEFAULT_PAGE_SIZE_MAIN_SEARCH_RESULTS!, offset: 0 } }));
  }
}

function* resetSearch(_: ActionType<typeof MainSearchActions.resetSearch>): SagaIterator {
  const scopes = (yield select(getMainSearchResultsScopes)) as MainSearchSource[];

  // clear results
  for (const scope of scopes) {
    // reset filter and results
    yield put(MainSearchActions.setFilterSetting({ payload: [], meta: { collection: scope } }));
    yield put(MainSearchActions.clearResults(null, { collection: scope }));
  }
}

function* fetchResults(action: ActionType<typeof MainSearchActions.fetchResults>): SagaIterator {
  const { collection, query } = action.meta;
  const rawQueryTerm = (yield select(getMainSearchText)) as string;
  const queryTerm = normalizeMainSearchTerm(rawQueryTerm, collection);
  const filters = (yield select(getSearchFiltersSetting(collection))) as IMainSearchFilterSetting[];
  try {
    yield put(MainSearchActions.fetchStart(null, { collection }));
    const lang = (yield select(getSelectedLanguage)) as string;
    const results = (yield call(mainSearchApi.fetchMainSearchResults, collection, queryTerm, filters, query, lang)) as IMainSearchResult;
    yield put(MainSearchActions.fetchSuccess(results, { collection, query }));

    const resCount = (yield select(getSearchResultCount, { collection })) as number;
    yield put(MainSearchActions.setMainSearchResultsCount(resCount));
    yield put(TrackerActions.trackMainSearchResults({ searchTerm: queryTerm, searchTopic: collection, resultCount: resCount }));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(MainSearchActions.fetchFail(err, { collection }));
  }
}

/**
 * Submits a single search tab
 * @param action
 */
function* submitSearchTab(action: ActionType<typeof MainSearchActions.submitSearchTab>): SagaIterator {
  const { collection } = action.meta;
  const currentQuery = (yield select(getPagingQuery, { collection })) as IPagingQuery;
  const offset = 0;
  const nextQuery: IPagingQuery = { ...currentQuery, offset };
  yield put(MainSearchActions.fetchResults(null, { collection, query: nextQuery }));
}

function* changePage(action: ActionType<typeof MainSearchActions.changePage>): SagaIterator {
  const { collection, targetPage } = action.payload;
  const currentQuery = (yield select(getPagingQuery, { collection })) as IPagingQuery;

  const offset = (targetPage - 1) * currentQuery.limit;
  const nextQuery: IPagingQuery = { ...currentQuery, offset };
  yield put(MainSearchActions.fetchResults(null, { collection, query: nextQuery }));
}

function* changePageSize(action: ActionType<typeof MainSearchActions.changePageSize>): SagaIterator {
  const { collection, limit } = action.payload;
  const offset = 0;
  const currentQuery = (yield select(getPagingQuery, { collection })) as IPagingQuery;
  const nextQuery: IPagingQuery = { ...currentQuery, offset, limit };
  yield put(MainSearchActions.fetchResults(null, { collection, query: nextQuery }));
}

function* mainSearchResultsSaga(): SagaIterator {
  yield debounce(500, getType(MainSearchActions.setSearchText), fetchSearchQuery);
  yield takeLeading(getType(MainSearchActions.resetSearch), resetSearch);
  yield takeEvery(getType(MainSearchActions.fetchResults), fetchResults);

  yield takeLatest(getType(MainSearchActions.changePage), changePage);
  yield takeLatest(getType(MainSearchActions.changePageSize), changePageSize);
  yield takeLatest(getType(MainSearchActions.submitSearchTab), submitSearchTab);
}

function* fetchSearchSuggestionQuery(_: ActionType<typeof MainSearchActions.setSuggestionsSearchText>): SagaIterator {
  const scopes = (yield select(getMainSearchSuggestionsScopes)) as MainSearchSuggestionsScope[];

  // clear suggestion results
  for (const scope of scopes) {
    yield put(MainSearchActions.clearSuggestionsResults(null, { collection: scope }));
  }

  // fetch new suggestion results
  for (const scope of scopes) {
    yield put(MainSearchActions.fetchSuggestionsResults(null, { collection: scope }));
  }
}

function* resetSearchSuggestions(_: ActionType<typeof MainSearchActions.resetSuggestionsSearch>): SagaIterator {
  const scopes = (yield select(getMainSearchSuggestionsScopes)) as MainSearchSuggestionsScope[];

  // clear suggestion results
  for (const scope of scopes) {
    yield put(MainSearchActions.clearSuggestionsResults(null, { collection: scope }));
  }
}

function* fetchSuggestionResults(action: ActionType<typeof MainSearchActions.fetchSuggestionsResults>): SagaIterator {
  const { collection } = action.meta;
  const rawQueryTerm = (yield select(getMainSearchSuggestionText)) as string;
  const queryTerm = collection === MainSearchSuggestionsScope.OemSpareParts ? rawQueryTerm : removeDots(rawQueryTerm);
  const lang = (yield select(getSelectedLanguage)) as string;

  const limit = 10;
  try {
    yield put(MainSearchActions.fetchSuggestionsStart(null, { collection }));

    const results = (yield call(mainSearchApi.fetchMainSearchSuggestions, collection, queryTerm, limit, lang)) as IMainSearchResult;
    yield put(MainSearchActions.fetchSuggestionsSuccess(results, { collection }));

    const resCount = (yield select(getMainSearchSuggestionResultsCount)) as number;
    yield put(MainSearchActions.setMainSearchSuggestionResultsCount(resCount));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(MainSearchActions.fetchSuggestionsFail(err, { collection }));
  }
}

function* mainSearchSuggestionSaga(): SagaIterator {
  yield debounce(500, getType(MainSearchActions.setSuggestionsSearchText), fetchSearchSuggestionQuery);
  yield takeLeading(getType(MainSearchActions.resetSuggestionsSearch), resetSearchSuggestions);
  yield takeEvery(getType(MainSearchActions.fetchSuggestionsResults), fetchSuggestionResults);
}

function* fetchFilterDefinition(action: ActionType<typeof MainSearchActions.fetchFilterDefinitions>): SagaIterator {
  const { collection } = action.meta;
  const meta = { collection };
  const rawTerm = (yield select(getMainSearchText)) as string;
  const term = normalizeMainSearchTerm(rawTerm, collection);
  try {
    yield put(MainSearchActions.fetchFilterDefinitionStart({ payload: null, meta }));
    const language = (yield select(getSelectedLanguage)) as string;
    const results = (yield call(mainSearchApi.fetchMainSearchFilterDefinition, collection, { term, language })) as IMainSearchFilterDefinition[];
    yield put(MainSearchActions.fetchFilterDefinitionSuccess({ payload: results, meta }));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    yield put(MainSearchActions.fetchFilterDefinitionFail({ payload: err, meta }));
  }
}

function* filterDefinitionSaga(): SagaIterator {
  yield takeLeading(getType(MainSearchActions.fetchFilterDefinitions), fetchFilterDefinition);
}

export function* mainSearchSaga(): SagaIterator {
  yield fork(mainSearchResultsSaga);
  yield fork(mainSearchSuggestionSaga);
  yield fork(filterDefinitionSaga);
}
